import {stripWeatherLayerPeriod} from '../../utils/weather-layer-name.util';

export interface LegendStop {
  value: string;
  color: string;
  /** Optional i18n key with the numeric range shown under a class label (docs/WEATHER.md → Legend). */
  rangeKey?: string;
  /** Interpolation params for `rangeKey`; thresholds come from exported constants, never from copy. */
  rangeParams?: Record<string, string | number>;
}

export interface LegendValueRange {
  min: number;
  max: number;
}

export interface ClippedLegend {
  items: LegendStop[];
  gradient: string | null;
  clipped: boolean;
}

export type WeatherLayerPeriod =
  | {kind: 'norm-day'; month: number; day: number}
  | {kind: 'daily'; year: number; month: number; day: number}
  | {kind: 'yearly'; year: number};

const REPORT_FIELDS: Record<string, string> = {
  Humidity: 'humidity',
  Fallout: 'fallout',
  SolarRadiationInfluence: 'solarRadiationInfluence',
  TemperatureAverage: 'temperatureAverage',
  TemperatureMax: 'temperatureMax',
  TemperatureMin: 'temperatureMin',
  TemperatureMaxTotal: 'temperatureMaxTotal',
  TemperatureMinTotal: 'temperatureMinTotal',
  TemperatureAverageTotal: 'temperatureAverage',
  SumOfFallout: 'sumOfFallout',
  SumOfSolarRadiation: 'sumOfSolarRadiation',
  SumOfActiveTemperaturesBase5: 'sumOfActiveTemperaturesBase5',
  SumOfActiveTemperaturesBase7: 'sumOfActiveTemperaturesBase7',
  SumOfActiveTemperaturesBase10: 'sumOfActiveTemperaturesBase10',
  SumOfActiveTemperaturesBase12: 'sumOfActiveTemperaturesBase12',
  SumOfActiveTemperaturesBase15: 'sumOfActiveTemperaturesBase15'
};

interface ParsedStop extends LegendStop {
  number: number;
  suffix: string;
  explicitPlus: boolean;
}

export function parseWeatherLayerPeriod(geoServerName: string): WeatherLayerPeriod | null {
  const daily = geoServerName.match(/(\d{4})(\d{2})(\d{2})$/);
  if (daily) {
    const month = Number(daily[2]);
    const day = Number(daily[3]);
    if (!isCalendarDay(month, day)) {
      return null;
    }
    return {kind: 'daily', year: Number(daily[1]), month, day};
  }

  const four = geoServerName.match(/(\d{4})$/);
  if (!four) {
    return null;
  }
  if (/Normal/i.test(geoServerName)) {
    const month = Number(four[1].slice(0, 2));
    const day = Number(four[1].slice(2, 4));
    if (!isCalendarDay(month, day)) {
      return null;
    }
    return {kind: 'norm-day', month, day};
  }

  const year = Number(four[1]);
  if (year < 1900 || year > 2200) {
    return null;
  }
  return {kind: 'yearly', year};
}

export function reportFieldForLayer(geoServerName: string): string | null {
  return REPORT_FIELDS[stripWeatherLayerPeriod(geoServerName)] ?? null;
}

export function readReportNumber(row: object, field: string): number | null {
  const value = (row as Record<string, unknown>)[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function rangeOf(values: number[]): LegendValueRange | null {
  const finite = values.filter((value) => Number.isFinite(value));
  if (!finite.length) {
    return null;
  }
  let min = finite[0];
  let max = finite[0];
  for (const value of finite) {
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
  }
  return {min, max};
}

export function deviationRange(
  reports: object[],
  norms: object[],
  field: string
): LegendValueRange | null {
  const normsByStation = new Map<string, number>();
  for (const norm of norms) {
    const station = stationId(norm);
    const value = readReportNumber(norm, field);
    if (!station || value == null) {
      continue;
    }
    normsByStation.set(station, value);
  }

  const diffs: number[] = [];
  for (const report of reports) {
    const station = stationId(report);
    const base = station ? normsByStation.get(station) : undefined;
    const value = readReportNumber(report, field);
    if (base == null || value == null) {
      continue;
    }
    diffs.push(value - base);
  }
  return rangeOf(diffs);
}

/** Yearly ATD weather tiles paint `sumOfFallout ?? fallout` and the year's `temperatureAverage`. */
export function yearlyWeatherRange(reports: object[], field: string): LegendValueRange | null {
  if (field !== 'sumOfFallout' && field !== 'temperatureAverage') {
    return null;
  }
  const values: number[] = [];
  for (const report of reports) {
    const value = field === 'sumOfFallout'
      ? readReportNumber(report, field) ?? readReportNumber(report, 'fallout')
      : readReportNumber(report, field);
    if (value != null) {
      values.push(value);
    }
  }
  return rangeOf(values);
}

export function yearlyDeviationRange(
  reports: object[],
  norms: object[],
  field: string
): LegendValueRange | null {
  if (field !== 'sumOfFallout' && field !== 'temperatureAverage') {
    return null;
  }

  const normValuesByStation = new Map<string, number[]>();
  for (const norm of norms) {
    const station = stationId(norm);
    const normField = field === 'sumOfFallout' ? 'fallout' : field;
    const value = readReportNumber(norm, normField);
    if (!station || value == null) {
      continue;
    }
    const values = normValuesByStation.get(station) ?? [];
    values.push(value);
    normValuesByStation.set(station, values);
  }

  const diffs: number[] = [];
  for (const report of reports) {
    const station = stationId(report);
    const normValues = station ? normValuesByStation.get(station) : undefined;
    if (!normValues?.length) {
      continue;
    }
    const reportValue = field === 'sumOfFallout'
      ? readReportNumber(report, field) ?? readReportNumber(report, 'fallout')
      : readReportNumber(report, field);
    if (reportValue == null) {
      continue;
    }
    const normValue = field === 'sumOfFallout'
      ? normValues.reduce((total, value) => total + value, 0)
      : normValues.reduce((total, value) => total + value, 0) / normValues.length;
    diffs.push(reportValue - normValue);
  }
  return rangeOf(diffs);
}

export function clipLegendToRange(items: LegendStop[], range: LegendValueRange): ClippedLegend {
  const parsed = items
    .map(parseStop)
    .filter((stop): stop is ParsedStop => stop != null)
    .sort((a, b) => a.number - b.number);
  if (parsed.length < 2) {
    return {items, gradient: null, clipped: false};
  }

  let min = range.min;
  let max = range.max;
  if (min > max) {
    const swap = min;
    min = max;
    max = swap;
  }

  if (Math.abs(max - min) < 1e-9) {
    const only = stopAt(min, parsed);
    return {items: [only], gradient: only.color, clipped: true};
  }

  const clipped: LegendStop[] = [stopAt(min, parsed)];
  for (const stop of parsed) {
    if (stop.number > min && stop.number < max) {
      clipped.push({value: stop.value, color: stop.color});
    }
  }
  clipped.push(stopAt(max, parsed));

  return {
    items: clipped,
    gradient: gradientCss(clipped, min, max),
    clipped: true
  };
}

export interface StationPoint {
  id: string;
  latitude: number;
  longitude: number;
}

/** Half of the ERA5 0.25° grid step: the station's painted cell reaches this far from its point. */
export const ERA5_HALF_CELL_DEGREES = 0.125;

/** Ids of stations whose ERA5 cell touches the painted area (`isInside(lat, lng)`). */
export function stationsInsideArea(
  stations: StationPoint[],
  isInside: (latitude: number, longitude: number) => boolean
): Set<string> {
  const d = ERA5_HALF_CELL_DEGREES;
  const offsets: Array<[number, number]> = [[0, 0], [d, 0], [-d, 0], [0, d], [0, -d], [d, d], [d, -d], [-d, d], [-d, -d]];
  const ids = new Set<string>();
  for (const station of stations) {
    if (offsets.some(([dLat, dLng]) => isInside(station.latitude + dLat, station.longitude + dLng))) {
      ids.add(station.id);
    }
  }
  return ids;
}

export function keepStations<T extends object>(rows: T[], stationIds: Set<string>): T[] {
  return rows.filter((row) => {
    const id = stationId(row);
    return id != null && stationIds.has(id);
  });
}

function stationId(row: object): string | null {
  const value = (row as Record<string, unknown>)['weatherStation'];
  return typeof value === 'string' && value ? value : null;
}

function isCalendarDay(month: number, day: number): boolean {
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

/** Display label for a gradient stop; compacts very large painted min/max (docs/WEATHER.md). */
export function formatLegendScaleLabel(value: string): string {
  const parsed = parseStop({value: value.trim(), color: '#000000'});
  if (!parsed) {
    return value.trim();
  }
  const n = parsed.number;
  const suffix = parsed.suffix.trim();
  const abs = Math.abs(n);
  const prefix = parsed.explicitPlus && n > 0 ? '+' : n < 0 ? '-' : '';

  let body: string;
  if (abs >= 1_000_000) {
    body = new Intl.NumberFormat(undefined, {notation: 'compact', maximumFractionDigits: 2}).format(n);
  } else if (abs >= 10_000 || String(Math.round(abs)).length > 5) {
    body = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(n);
  } else if (abs >= 1000) {
    body = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(n);
  } else {
    return value.trim();
  }

  if (prefix === '+' && !body.startsWith('+') && !body.startsWith('-')) {
    return `${prefix}${body}${suffix}`;
  }
  return `${body}${suffix}`;
}

function parseStop(item: LegendStop): ParsedStop | null {
  const match = item.value.trim().match(/^([+-])?((?:\d[\d\s]*)(?:[.,]\d+)?)(.*)$/);
  if (!match || !match[2]) {
    return null;
  }
  const numeric = Number(match[2].replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(numeric)) {
    return null;
  }
  const signed = match[1] === '-' ? -numeric : numeric;
  return {
    ...item,
    number: signed,
    suffix: match[3] ?? '',
    explicitPlus: match[1] === '+'
  };
}

function stopAt(value: number, catalog: ParsedStop[]): LegendStop {
  const exact = catalog.find((stop) => Math.abs(stop.number - value) < 1e-6);
  if (exact) {
    return {value: exact.value, color: exact.color};
  }
  return {value: formatBound(value, catalog), color: colorAt(value, catalog)};
}

function formatBound(value: number, catalog: ParsedStop[]): string {
  const suffix = catalog.every((stop) => stop.suffix === catalog[0].suffix) ? catalog[0].suffix : '';
  const plus = catalog.some((stop) => stop.explicitPlus && stop.number > 0);
  const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  const body = String(rounded);
  const positive = rounded > 0 && plus ? `+${body}` : body;
  return `${positive}${suffix}`;
}

function colorAt(value: number, catalog: ParsedStop[]): string {
  if (value <= catalog[0].number) {
    return catalog[0].color;
  }
  const last = catalog[catalog.length - 1];
  if (value >= last.number) {
    return last.color;
  }
  for (let index = 1; index < catalog.length; index++) {
    const upper = catalog[index];
    const lower = catalog[index - 1];
    if (value > upper.number) {
      continue;
    }
    const span = upper.number - lower.number;
    const ratio = span === 0 ? 0 : (value - lower.number) / span;
    return mixHex(lower.color, upper.color, ratio);
  }
  return last.color;
}

function gradientCss(items: LegendStop[], min: number, max: number): string {
  const span = max - min;
  const stops = items.map((item) => {
    const parsed = parseStop(item);
    const ratio = parsed ? (parsed.number - min) / span : 0;
    const percent = Math.round(Math.min(1, Math.max(0, ratio)) * 1000) / 10;
    return `${item.color} ${percent}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

function mixHex(from: string, to: string, ratio: number): string {
  const start = hexRgb(from);
  const end = hexRgb(to);
  const channel = (index: number) => Math.round(start[index] + (end[index] - start[index]) * ratio);
  return `#${toHex(channel(0))}${toHex(channel(1))}${toHex(channel(2))}`;
}

function hexRgb(color: string): [number, number, number] {
  const hex = color.replace('#', '');
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16)
  ];
}

function toHex(channel: number): string {
  return Math.min(255, Math.max(0, channel)).toString(16).padStart(2, '0');
}
